import type { ThemeType } from '@/config/objects/theme-types';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  Input,
  Skeleton,
  Textarea,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

interface ThemeTypeFormData {
  name: string;
  slug: string;
  description: string;
  tone: string;
}

const DEFAULT_FORM: ThemeTypeFormData = {
  name: '',
  slug: '',
  description: '',
  tone: '',
};

export function ThemeTypeFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<ThemeTypeFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  const updateField = useCallback(
    <K extends keyof ThemeTypeFormData>(key: K, value: ThemeTypeFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // Load existing theme type (edit mode)
  const {
    data: themeTypeResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: ThemeType }>({
    queryKey: ['ThemeType', itemId],
    queryFn: () => apiClient.get(`/theme-types/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!themeTypeResponse?.data) return;
    const a = themeTypeResponse.data;
    setFormData({
      name: a.name ?? '',
      slug: a.slug ?? '',
      description: a.description ?? '',
      tone: a.tone ?? '',
    });
    setLastSaved(new Date(a.updatedAt));
    setFormReady(true);
  }, [themeTypeResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Theme type not found');
      navigate('/theme-types');
    }
  }, [isError, navigate]);

  // Validation
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.slug.trim()) {
      errs.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug.trim())) {
      errs.slug = 'Slug must contain only lowercase letters, numbers, and hyphens';
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`field-${firstKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  // Prepare payload
  const preparePayload = () => ({
    name: formData.name.trim(),
    slug: formData.slug.trim(),
    description: formData.description || null,
    tone: formData.tone || null,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: ThemeType }>(`/theme-types/${itemId}`, payload);
      }
      return apiClient.post<{ data: ThemeType }>('/theme-types', payload);
    },
    onSuccess: (response: { data: ThemeType }) => {
      queryClient.invalidateQueries({ queryKey: ['ThemeType'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Theme type saved successfully');
        navigate(-1);
      } else {
        toast.success('Theme type created successfully');
        navigate(`/theme-types/${response.data.id}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save theme type');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;
    saveMutation.mutate(preparePayload());
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/theme-types/${itemId}`);
    } else {
      navigate('/theme-types');
    }
  };

  // Loading skeleton
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Theme Type' : 'Create Theme Type'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? formData.name || 'Update theme type details'
                : 'Add a new theme type to the platform'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div ref={firstErrorRef} className="space-y-6">
          {/* General Information */}
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 border-b">
              <CardTitle className="text-zinc-900">General Information</CardTitle>
              <CardDescription>Core theme type details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter theme type name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="slug"
                label="Slug"
                required
                error={errors.slug}
                description="URL-friendly identifier using lowercase letters, numbers, and hyphens"
              >
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => updateField('slug', e.target.value)}
                  placeholder="e.g., adventure-theme"
                  className={cn(errors.slug && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="description" label="Description">
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Enter a description for this theme type"
                  rows={4}
                />
              </FormFieldGroup>

              <FormFieldGroup name="tone" label="Tone">
                <Textarea
                  id="tone"
                  value={formData.tone}
                  onChange={(e) => updateField('tone', e.target.value)}
                  placeholder="Describe the tone for this theme type"
                  rows={4}
                />
              </FormFieldGroup>
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
